import type { DateFieldConfiguration } from '@atlassian/jira-polaris-remote-issue/src/services/jira/types.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type { FieldKey } from '@atlassian/jira-polaris-domain-field/src/field/types.tsx';
import {
	getDeliveryCalculationMode,
	getDeliveryCalculationStrategy,
} from '../../selectors/fields.tsx';
import { getLocalIssueIdsByJiraIssueId } from '../../selectors/issue-ids.tsx';
import {
	getSelectedIssueLocalIssueId,
	getSelectedIssueJiraId,
	createGetIdSelector,
} from '../../selectors/properties/index.tsx';
import { createGetProject } from '../../selectors/properties/project/index.tsx';
import type { Props, State } from '../../types.tsx';
import {
	createGetConnectionsValueSelector,
	createGetConnectionsValuesMap,
	createGetIsConnectionFieldThatNeedsDeliveryData,
	getConnectionFieldsThatNeedDeliveryData,
} from '../../selectors/connection.tsx';
import { setLoadingLinkedIssuesState } from '../utils/index.tsx';
import { fireErrorAnalytics } from './fire-error-analytics/index.tsx';
import { getFieldConfigurations } from './get-field-configurations/index.tsx';
import { loadAggregatedDeliveryProgress } from './load-aggregated-delivery-progress/index.tsx';
import type { DeliveryAnalyticsAttributes } from './types.tsx';
import { replaceOmitRecordKeys } from './load-aggregated-delivery-progress/replace-omit-record-keys/index.tsx';

export const loadDeliveryProgress =
	(
		providedDeliveryStrategy?: 'default' | 'simple',
		calculationMode?: 'issue_count' | 'story_points',
		issueIds?: IssueId[],
		archivedOnly?: boolean,
	) =>
	async ({ getState, setState, dispatch }: StoreActionApi<State>, props: Props) => {
		if (fg('jpd_support-delivery-data-loading-connected-issues')) {
			if (props.isSharedView) {
				dispatch(
					loadAggregatedDeliveryProgress(
						true,
						providedDeliveryStrategy,
						calculationMode,
						issueIds,
						archivedOnly,
					),
				);
				return;
			}

			if (issueIds && issueIds.length > 0) {
				dispatch(
					refreshDeliveryProgressForSpecificIssues(issueIds, { shouldLoadLinkedIssues: true }),
				);
			} else {
				setState(setLoadingLinkedIssuesState(getState(), true));
				await Promise.all([
					dispatch(
						loadAggregatedDeliveryProgress(
							false,
							providedDeliveryStrategy,
							calculationMode,
							issueIds,
							archivedOnly,
						),
					),
					dispatch(loadDeliveryProgressForCrossProjectIssues()),
				]);

				setState(setLoadingLinkedIssuesState(getState(), false));
			}
		} else {
			dispatch(
				loadAggregatedDeliveryProgress(
					true,
					providedDeliveryStrategy,
					calculationMode,
					issueIds,
					archivedOnly,
				),
			);
		}
	};

export const refreshDeliveryProgressForSelectedIssue =
	(options?: { includeConnections?: boolean }) =>
	({ dispatch, getState }: StoreActionApi<State>, props: Props) => {
		const { isSharedView } = props;

		if (!fg('jpd-connections-support-on-published-views')) {
			if (isSharedView) {
				return;
			}
		}

		const selectedIssueLocalIssueId = getSelectedIssueLocalIssueId(getState());

		if (!selectedIssueLocalIssueId) {
			return;
		}

		const jiraId = getSelectedIssueJiraId(getState());
		if (jiraId === undefined) {
			return;
		}

		if (fg('jpd_support-delivery-data-loading-connected-issues')) {
			const issueIdsToRefresh: IssueId[] = [String(jiraId)];

			if (options?.includeConnections) {
				const connectionFieldsWithHighlightedDeliveryField =
					getConnectionFieldsThatNeedDeliveryData(getState(), props);

				const connectedIssueIds = connectionFieldsWithHighlightedDeliveryField?.reduce<IssueId[]>(
					(acc, { key: connectionFieldKey }) => {
						const connectionsForIssue =
							createGetConnectionsValueSelector(connectionFieldKey, selectedIssueLocalIssueId)(
								getState(),
								props,
							)?.map(({ id }) => id) ?? [];

						acc.push(...connectionsForIssue);

						return acc;
					},
					[],
				);

				issueIdsToRefresh.push(...new Set(connectedIssueIds));
			}

			dispatch(refreshDeliveryProgressForSpecificIssues(issueIdsToRefresh));
		} else {
			dispatch(refreshDeliveryProgressForSpecificIssue(String(jiraId)));
		}
	};

// Remove when cleaning up `jpd_support-delivery-data-loading-connected-issues` (currentlye exported for tests)
export const refreshDeliveryProgressForSpecificIssue =
	(issueId: IssueId) =>
	({ getState, setState }: StoreActionApi<State>, props: Props) => {
		const { fields, projectId, isSharedView, isCollectionView } = props;

		if (isSharedView) {
			return;
		}

		if (issueId === undefined) {
			return;
		}

		const localIssueId = getLocalIssueIdsByJiraIssueId(getState(), props)[issueId];
		if (!localIssueId) {
			return;
		}

		const safeProjectId = isCollectionView
			? createGetProject(localIssueId)(getState())?.id
			: projectId;

		if (safeProjectId === undefined) {
			return;
		}

		const calculationMode = getDeliveryCalculationMode(getState(), props);
		const calculationStrategy = getDeliveryCalculationStrategy(getState(), props);
		const analyticsAttributes: DeliveryAnalyticsAttributes = {
			deliveryDatesCount: 0,
			deliveryDatesPresent: false,
			calculationMode,
		};

		const dateFieldConfigurations: DateFieldConfiguration[] = getFieldConfigurations(fields);
		if (dateFieldConfigurations.length > 0) {
			analyticsAttributes.deliveryDatesCount = dateFieldConfigurations.length;
			analyticsAttributes.deliveryDatesPresent = true;
		}

		const fetchAggregatedStartTimestamp = Date.now();
		props.issuesRemote
			.fetchAggregatedDeliveryProgress({
				projectId: safeProjectId,
				issueIds: [issueId],
				calculationStrategy,
				calculationMode,
				dateFieldConfigurations,
			})
			.then(({ ideaToProgressAggregation, ideaDateAggregation }) => {
				const aggregatedDeliveryDates = {
					...getState().properties.aggregatedDeliveryDates,
				};

				if (ideaDateAggregation && ideaDateAggregation[issueId]) {
					aggregatedDeliveryDates[localIssueId] = ideaDateAggregation[issueId];
				}

				setState({
					properties: {
						...getState().properties,
						aggregatedDeliveryProgress: {
							...getState().properties.aggregatedDeliveryProgress,
							[localIssueId]: ideaToProgressAggregation[issueId],
						},
						aggregatedDeliveryDates,
					},
				});
			})
			.catch((error) => {
				fireErrorAnalytics(props.createAnalyticsEvent, fetchAggregatedStartTimestamp);
				props.onDeliveryDataFetchFailed(error);
			});

		fireTrackAnalytics(props.createAnalyticsEvent({}), 'deliveryData loaded', analyticsAttributes);
	};

export const refreshDeliveryProgressForSpecificIssues =
	(issueIds: IssueId[], options?: { shouldLoadLinkedIssues?: boolean }) =>
	async ({ getState, setState }: StoreActionApi<State>, props: Props) => {
		if (issueIds.length === 0) {
			return;
		}

		if (options?.shouldLoadLinkedIssues) {
			setState(setLoadingLinkedIssuesState(getState(), true));
		}

		const analyticsAttributes: DeliveryAnalyticsAttributes = {
			deliveryDatesCount: 0,
			deliveryDatesPresent: false,
		};

		const dateFieldConfigurations: DateFieldConfiguration[] = getFieldConfigurations(props.fields);
		if (dateFieldConfigurations.length > 0) {
			analyticsAttributes.deliveryDatesCount = dateFieldConfigurations.length;
			analyticsAttributes.deliveryDatesPresent = true;
		}

		const fetchAggregatedStartTimestamp = Date.now();

		try {
			const response = await props.issuesRemote.fetchAggregatedDeliveryProgressForIssues({
				issueIds,
				dateFieldConfigurations,
			});

			const jiraIdToLocalIdMap = getLocalIssueIdsByJiraIssueId(getState(), props);

			const aggregatedDeliveryProgressByLocalId = replaceOmitRecordKeys(
				response.ideaToProgressAggregation,
				jiraIdToLocalIdMap,
			);

			const aggregatedDeliveryDatesByLocalId = replaceOmitRecordKeys(
				response.ideaDateAggregation,
				jiraIdToLocalIdMap,
			);

			setState({
				properties: {
					...getState().properties,
					aggregatedDeliveryProgress: {
						...getState().properties.aggregatedDeliveryProgress,
						...aggregatedDeliveryProgressByLocalId,
					},
					aggregatedDeliveryDates: {
						...getState().properties.aggregatedDeliveryDates,
						...aggregatedDeliveryDatesByLocalId,
					},
				},
				meta: {
					...getState().meta,
					loadingLinkedIssues: false,
				},
			});

			fireTrackAnalytics(
				props.createAnalyticsEvent({}),
				'deliveryData loaded',
				analyticsAttributes,
			);
		} catch (error) {
			fireErrorAnalytics(props.createAnalyticsEvent, fetchAggregatedStartTimestamp);
			setState(setLoadingLinkedIssuesState(getState(), false));
			props.onDeliveryDataFetchFailed(
				error instanceof Error
					? error
					: new Error('Error fetching delivery data for specific issues'),
			);
		}
	};

export const loadDeliveryProgressForCrossProjectIssues =
	() =>
	async ({ getState, dispatch }: StoreActionApi<State>, props: Props) => {
		const connectionIssueIds = getState().connectionIssueIds;
		if (!connectionIssueIds || connectionIssueIds.length === 0) {
			return;
		}

		const connectionFieldsWithHighlightedDeliveryField = getConnectionFieldsThatNeedDeliveryData(
			getState(),
			props,
		);
		if (connectionFieldsWithHighlightedDeliveryField.length === 0) {
			return;
		}

		const connectionJiraIssueIds = connectionIssueIds.map((localIssueId) =>
			createGetIdSelector(localIssueId)(getState()),
		);

		const jiraIdsToLoadDeliveryData = connectionFieldsWithHighlightedDeliveryField.reduce<
			IssueId[]
		>((acc, { key: connectionFieldKey }) => {
			if (!connectionFieldKey) {
				return acc;
			}

			const connectionsMap = createGetConnectionsValuesMap(connectionFieldKey)(getState(), props);

			if (!connectionsMap) {
				return acc;
			}

			Object.values(connectionsMap).forEach((connectionFieldValue) => {
				if (!connectionFieldValue) {
					return;
				}

				const matchingIssueIds = connectionFieldValue
					.map(({ id }) => id)
					.filter((issueId) => connectionJiraIssueIds.includes(parseInt(issueId, 10)));
				acc.push(...matchingIssueIds);
			});

			return acc;
		}, []);

		if (jiraIdsToLoadDeliveryData.length === 0) {
			return;
		}

		await dispatch(
			refreshDeliveryProgressForSpecificIssues(Array.from(new Set(jiraIdsToLoadDeliveryData))),
		);
	};

export const initDeliveryProgress =
	() =>
	({ dispatch }: StoreActionApi<State>, props: Props) => {
		if (props.singleIdeaVisible) {
			dispatch(refreshDeliveryProgressForSelectedIssue({ includeConnections: true }));
		} else {
			dispatch(loadAggregatedDeliveryProgress());
			if (!props.isSharedView && fg('jpd_support-delivery-data-loading-connected-issues')) {
				dispatch(loadDeliveryProgressForCrossProjectIssues());
			}
		}
	};

export const refreshDeliveryDataForConnectionField =
	(connectionFieldKey: FieldKey) =>
	async ({ getState, dispatch }: StoreActionApi<State>, props: Props) => {
		const connectionIssueIds = getState().connectionIssueIds;
		if (!connectionIssueIds || connectionIssueIds.length === 0) {
			return;
		}

		const isConnectionFieldThatNeedsDeliveryData = createGetIsConnectionFieldThatNeedsDeliveryData(
			connectionFieldKey,
		)(getState(), props);

		if (!isConnectionFieldThatNeedsDeliveryData) {
			return;
		}

		const connectionsMap = createGetConnectionsValuesMap(connectionFieldKey)(getState(), props);

		if (!connectionsMap) {
			return;
		}

		const connectionJiraIssueIds = connectionIssueIds.map((localIssueId) =>
			createGetIdSelector(localIssueId)(getState()),
		);

		const jiraIdsToLoadDeliveryData = Object.values(connectionsMap).reduce<IssueId[]>(
			(acc, connectionFieldValue) => {
				if (!connectionFieldValue) {
					return acc;
				}

				const matchingIssueIds = connectionFieldValue
					.map(({ id }) => id)
					.filter((issueId) => connectionJiraIssueIds.includes(parseInt(issueId, 10)));
				acc.push(...matchingIssueIds);

				return acc;
			},
			[],
		);

		if (jiraIdsToLoadDeliveryData.length === 0) {
			return;
		}

		await dispatch(
			refreshDeliveryProgressForSpecificIssues(Array.from(new Set(jiraIdsToLoadDeliveryData))),
		);
	};
